// Archive/index.jsx
import React, { useState } from 'react';
import './index.css';

const Archive = () => {
  // 文章数据
  const initialArticles = [
    {
      id: 1,
      title: '恒星·宇宙的主角',
      date: '2025-12-08',
      tags: ['宇宙', '恒星'],
      summary: '纵观宇宙，我找到了属于她的主角。',
      fullContent: `在浩瀚无垠的宇宙中，恒星无疑是真正的主角。它们不仅仅是夜空中的亮点，更是宇宙演化和生命存在的关键。

## 恒星的诞生

恒星诞生于巨大的分子云中，当这些云团因为引力作用而坍缩时，内部的温度和压力会急剧上升。当核心温度达到足够高时，氢核聚变反应开始，一颗新的恒星就此诞生。

## 恒星的分类

根据质量、温度和亮度，恒星可以分为多种类型：
- **主序星**：像太阳一样，处于稳定燃烧阶段的恒星
- **红巨星**：燃料即将耗尽的老年恒星
- **白矮星**：中小质量恒星演化的最终产物
- **中子星和黑洞**：大质量恒星演化的结果

## 恒星的意义

恒星不仅仅是宇宙的"灯塔"，它们还创造了宇宙中大部分的重元素。通过核聚变过程，恒星将氢和氦转化为碳、氧、铁等更重的元素，这些元素最终成为行星、生命甚至我们身体的组成部分。

当我们仰望星空时，我们看到的不仅是光，更是宇宙的历史和未来。每一颗恒星都在诉说着宇宙的故事。`,
      isExpanded: false
    },
    {
      id: 2,
      title: '两颗恒星的十万年',
      date: '2025-12-19',
      tags: ['宇宙', '恒星', '光年'],
      summary: '一起相伴的两颗恒星，跨越光年。',
      fullContent: `在宇宙的某个角落，存在着一种特殊的恒星系统——双星系统。其中两颗恒星在引力的作用下相互环绕，共同度过了漫长的岁月。

## 双星系统的形成

双星系统通常形成于同一个分子云中。当云团分裂成多个部分时，如果条件合适，就可能形成两颗相互环绕的恒星。根据估计，银河系中超过一半的恒星都存在于多星系统中。

## 十万年的相伴

想象一下，两颗恒星在十万年的时光里始终相伴。它们之间的距离可能只有几个天文单位，也可能相隔数千天文单位。但无论距离多远，它们都在引力的纽带下保持着联系。

## 双星系统的类型

根据观测方式，双星系统可以分为：
1. **目视双星**：通过望远镜可以直接分辨的两颗恒星
2. **光谱双星**：通过光谱分析发现的双星
3. **食双星**：由于轨道平面接近视线方向，会相互遮掩的双星
4. **天体测量双星**：通过恒星位置的周期性变化发现的双星

## 科学意义

研究双星系统对于天文学有着重要意义：
- 可以准确测量恒星的质量
- 有助于理解恒星的形成和演化
- 某些特殊现象（如新星爆发）只发生在双星系统中

## 浪漫的宇宙之舞

每对双星都在进行着宇宙的舞蹈，它们的轨道周期从几天到几千年不等。有些双星最终会合并，产生壮观的天文现象；有些则会逐渐分离，结束漫长的相伴。

在宇宙的尺度上，十万年或许只是短暂的一瞬，但对于人类来说，这已经是文明史的数倍。而这样的相伴，在宇宙中无处不在，诉说着宇宙既宏大又亲密的双重特性。`,
      isExpanded: false
    },
    {
      id: 3,
      title: '黑洞：宇宙的终结者',
      date: '2025-12-05',
      tags: ['宇宙', '黑洞', '引力'],
      summary: '探索宇宙中最神秘的天体——黑洞。',
      fullContent: `黑洞是宇宙中最极端的天体之一，它的引力如此之强，连光都无法逃脱。这些宇宙的"终结者"既是毁灭者，也是创造者。`,
      isExpanded: false
    },
    {
      id: 4,
      title: '星系演化的奥秘',
      date: '2025-11-28',
      tags: ['宇宙', '星系', '演化'],
      summary: '从原始星系到成熟星系，宇宙的结构是如何形成的？',
      fullContent: `星系的形成和演化是宇宙学中的核心问题。从宇宙大爆炸后的微小扰动，到今天我们看到的各种类型的星系，这个过程跨越了数十亿年。`,
      isExpanded: false
    }
  ];

  const [articles, setArticles] = useState(initialArticles);

  // 处理阅读更多/收起
  const handleReadMore = (articleId) => {
    setArticles(prevArticles => 
      prevArticles.map(article => ({
        ...article,
        isExpanded: article.id === articleId ? !article.isExpanded : false
      }))
    );
  };

  // 格式化日期
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="archive-container">
      <header className="archive-header">
        <h1 className="archive-title">最新文章</h1>
        <p className="archive-subtitle">探索宇宙的奥秘，发现星空的魅力</p>
        <div className="archive-stats">
          <span className="stat-item">共 {articles.length} 篇文章</span>
          <span className="stat-divider">•</span>
          <span className="stat-item">最近更新: {formatDate(articles[0].date)}</span>
        </div>
      </header>

      <div className="articles-list">
        {articles.map(article => (
          <article key={article.id} className="article-card">
            <div className="article-header">
              <div className="article-meta">
                <time className="article-date">{formatDate(article.date)}</time>
                <div className="article-tags">
                  {article.tags.map((tag, index) => (
                    <span key={index} className="article-tag">{tag}</span>
                  ))}
                </div>
              </div>
              <h2 className="article-title">{article.title}</h2>
            </div>

            <div className="article-summary">
              <p>{article.summary}</p>
            </div>

            {/* 展开的完整内容 */}
            {article.isExpanded && (
              <div className="article-full-content">
                <div className="content-divider">
                  <span className="divider-text">文章正文</span>
                </div>
                <div className="article-content">
                  {article.fullContent.split('\n\n').map((paragraph, index) => {
                    // 检查是否是标题
                    if (paragraph.startsWith('## ')) {
                      return <h3 key={index} className="content-subtitle">{paragraph.substring(3)}</h3>;
                    } else if (paragraph.startsWith('1. ') || paragraph.includes('- **')) {
                      // 处理列表项
                      const lines = paragraph.split('\n');
                      return (
                        <ul key={index} className="content-list">
                          {lines.map((line, lineIndex) => (
                            <li key={lineIndex} className="content-list-item">
                              {line.replace(/^[0-9]+\.\s+|- \*\*/g, '').replace(/\*\*/g, '')}
                            </li>
                          ))}
                        </ul>
                      );
                    } else {
                      return <p key={index} className="content-paragraph">{paragraph}</p>;
                    }
                  })}
                </div>
              </div>
            )}

            <div className="article-footer">
              <div className="article-actions">
                <button 
                  className={`read-more-btn ${article.isExpanded ? 'expanded' : ''}`}
                  onClick={() => handleReadMore(article.id)}
                >
                  {article.isExpanded ? '收起内容' : '阅读更多'}
                  <span className="btn-icon">{article.isExpanded ? '↑' : '↓'}</span>
                </button>
                <button className="share-btn">
                  <span className="share-icon">↗</span>
                  分享
                </button>
              </div>
              
              {!article.isExpanded && (
                <div className="article-preview-hint">
                  <span className="hint-icon">✨</span>
                  <span>点击"阅读更多"查看完整内容</span>
                </div>
              )}
            </div>

            {article.id < articles.length && <div className="article-divider"></div>}
          </article>
        ))}
      </div>

      <footer className="archive-footer">
        <p className="footer-text">
          探索更多宇宙奥秘
          <span className="footer-icon">🌌</span>
        </p>
        <button className="load-more-btn">加载更多文章</button>
      </footer>
    </div>
  );
};

export default Archive;